/**
 * Acquéreur personne morale — détection et conséquences sur les pièces attendues.
 *
 * Source de vérité UNIQUE, partagée par les trois endroits qui décidaient
 * jusqu'ici chacun de leur côté quelles pièces sont requises :
 *   • FormCGIAv2            → checklist agence (affichage)
 *   • RecapAgencyScreen     → `missingDocTypes` envoyé au backend, qui crée les
 *                             `cg_document_requests` et pilote `cg_review_status`
 *   • CGReview/PiecesGrid   → grille de relecture GOD (cartes « Manquante »)
 *
 * Sans ce partage, un dossier acquéreur personne morale sortait de la checklist
 * agence correctement (permis / justificatif de domicile masqués) mais se voyait
 * quand même créer deux demandes de pièces IMPOSSIBLES à satisfaire et rester
 * bloqué en « Pièces à déposer » — retour François 2026-08-06 sur EH-214-VH
 * (acquéreur BOYER-LEGRIX), reproduit sur 5 dossiers en attente.
 *
 * Règle métier (référentiel CTVO §2.4, confirmée par François) — acquéreur
 * personne morale en parcours simple :
 *   • Kbis                 : REQUIS (identifie ET domicilie la société)
 *   • Pièce d'identité     : REQUISE — celle d'un représentant figurant au Kbis
 *   • Justificatif domicile: NON requis — le Kbis fait foi via l'adresse de
 *                            l'établissement
 *   • Permis de conduire   : NON requis — une société n'a pas de permis
 */
import type { CgDocType } from '../ocr/ocrResponseTypes';

/**
 * Pièces du parcours particulier qui ne s'appliquent pas à une société.
 * `kbis` les remplace (ajouté à la file par `buildEffectiveDocQueue`).
 */
export const PM_NOT_REQUIRED_DOC_TYPES: readonly CgDocType[] = ['permis_conduire', 'justif_domicile'];

export interface AcheteurPersonneMoraleSignals {
  /** `acheteur_est_personne_morale` du Cerfa de cession (le signal le plus direct). */
  flagCession?: unknown;
  /**
   * Un Kbis DE L'ACQUÉREUR a été déposé / analysé.
   *
   * ⚠️ Depuis l'ouverture d'un slot Kbis vendeur, ce booléen ne doit JAMAIS être
   * alimenté par la simple présence d'un Kbis dans le dossier : le Kbis d'un
   * vendeur professionnel ferait alors basculer l'ACQUÉREUR en personne morale,
   * et `PM_NOT_REQUIRED_DOC_TYPES` lui retirerait son permis et son justificatif
   * de domicile — soit exactement le bug d'août 2026, à l'envers. N'accepter ici
   * qu'un `kbis_acheteur`, ou un `kbis` non routé (dossiers antérieurs).
   */
  hasKbis?: boolean;
  /** `acheteur_siret` (cession) ou `siret` (Kbis acquéreur). */
  acheteurSiret?: unknown;
  /** `acheteur_raison_sociale` (cession) ou `raison_sociale` (Kbis acquéreur). */
  acheteurRaisonSociale?: unknown;
}

/**
 * Signaux du VENDEUR personne morale, symétriques de ceux de l'acquéreur.
 *
 * Le vendeur professionnel n'était jusqu'ici détecté qu'au fil de l'eau, par des
 * champs épars (`isCessionVendeurPro` dans crossCheckRules, `sellerIsPro` dans
 * mergeExtractedFieldsToFormValues). Son Kbis n'est PAS une pièce exigée par le
 * SIV : il ne sert qu'à pré-remplir la raison sociale et le SIRET du vendeur
 * dans la modale « Soumettre le CTVO ». Il n'entre donc ni dans les pièces
 * requises, ni dans les demandes de pièces adressées à l'agence.
 */
export interface VendeurPersonneMoraleSignals {
  /** `vendeur_est_personne_morale` du Cerfa de cession. */
  flagCession?: unknown;
  /** Un Kbis DU VENDEUR a été déposé / analysé. */
  hasKbisVendeur?: boolean;
  /** `vendeur_siret` de la cession. */
  vendeurSiret?: unknown;
  /** `vendeur_raison_sociale` de la cession. */
  vendeurRaisonSociale?: unknown;
  /** `titulaire_siren` du C.1 de la carte grise — le titulaire actuel EST le vendeur. */
  titulaireSiren?: unknown;
}

const hasText = (value: unknown): boolean => typeof value === 'string' && value.trim().length > 0;

/**
 * Nature d'une partie, telle qu'une SEULE règle la tranche pour tout le parcours.
 *
 * `'indecis'` n'est pas un échec : c'est le verdict honnête quand les signaux se
 * CONTREDISENT — la cession déclare un particulier alors que des identifiants
 * d'entreprise ont été lus, ou l'inverse. Aucune des deux faces n'a raison a
 * priori : sur GP-482-JC (retour François 2026-09-17) la cession portait
 * `vendeur_est_personne_morale: false` à 0.88 ET un `vendeur_siret` à 0.75, le
 * vendeur PROCELL étant bel et bien une société. Trancher à pile ou face aurait
 * masqué à l'écran soit le nom, soit le SIRET — et c'est exactement ce qui s'est
 * produit : ni l'agence ni le relecteur ne voyaient le SIRET, donc personne ne
 * pouvait corriger ce qui partait en base.
 */
export type NatureEntite = 'particulier' | 'pm' | 'indecis';

/**
 * Règle unique, appliquée aux deux parties.
 *
 * Deux axes indépendants :
 *  • le FLAG déclaré sur la cession (`*_est_personne_morale`) — une case à
 *    cocher, lue par l'OCR, faillible dans les deux sens ;
 *  • les IDENTIFIANTS d'entreprise réellement lus au dossier (SIRET, raison
 *    sociale, SIREN du C.1 de la carte grise, Kbis de CETTE partie) — une
 *    société ne s'invente pas un SIRET.
 *
 *          │ identifiants présents │ aucun identifiant
 *  ────────┼───────────────────────┼───────────────────
 *  flag ✓  │ pm                    │ pm
 *  flag ✗  │ INDÉCIS               │ particulier
 *  inconnu │ pm                    │ particulier
 *
 * Le seul cas litigieux est donc « flag à faux MALGRÉ des identifiants ».
 */
const trancher = (flagCession: unknown, identifiants: readonly unknown[]): NatureEntite => {
  const aDesIdentifiants = identifiants.some((v) => (typeof v === 'boolean' ? v : hasText(v)));
  if (flagCession === true) return 'pm';
  if (!aDesIdentifiants) return 'particulier';
  return flagCession === false ? 'indecis' : 'pm';
};

/** Nature de l'ACQUÉREUR. */
export const natureAcheteur = ({
  flagCession,
  hasKbis,
  acheteurSiret,
  acheteurRaisonSociale,
}: AcheteurPersonneMoraleSignals): NatureEntite =>
  trancher(flagCession, [Boolean(hasKbis), acheteurSiret, acheteurRaisonSociale]);

/** Nature du VENDEUR. Le SIREN du C.1 compte : un titulaire immatriculé sous un
 *  SIREN est une société, même si la cession ne porte aucune raison sociale. */
export const natureVendeur = ({
  flagCession,
  hasKbisVendeur,
  vendeurSiret,
  vendeurRaisonSociale,
  titulaireSiren,
}: VendeurPersonneMoraleSignals): NatureEntite =>
  trancher(flagCession, [Boolean(hasKbisVendeur), vendeurSiret, vendeurRaisonSociale, titulaireSiren]);

/**
 * « Faut-il traiter cette partie comme une société pour les PIÈCES ? »
 *
 * Dérivé de la règle unique, volontairement PRUDENT : un dossier indécis est
 * traité comme une société. Réclamer un Kbis qui s'avérera inutile se corrige
 * d'un clic ; ne pas le réclamer bloque le dossier à l'ANTS. C'est le
 * comportement d'avant l'unification (quatre signaux en OU), à l'identique.
 *
 * Cas DC-39988 (SCEA LA FERME DE PLACHAMP) : seul le Kbis déposé permettait de
 * trancher. Cas EH-214-VH : seul le booléen de la cession, à 0.95 de confiance.
 */
export const isAcheteurPersonneMorale = (signals: AcheteurPersonneMoraleSignals): boolean =>
  natureAcheteur(signals) !== 'particulier';

/** Idem côté vendeur. */
export const isVendeurPersonneMorale = (signals: VendeurPersonneMoraleSignals): boolean =>
  natureVendeur(signals) !== 'particulier';


/**
 * Nature IMPOSÉE par un humain, quand la lecture des pièces se trompe ou n'a
 * rien pu trancher.
 *
 * La règle ci-dessus lit des documents ; elle ne peut pas savoir qu'un Kbis a
 * été déposé par erreur, ni qu'un vendeur dont la cession porte un SIRET vend
 * en réalité son véhicule personnel. Retour François du 2026-09-18 : « dans
 * CGIA nous n'avons pas le moyen de basculer d'une personne morale à physique ».
 * La modale god « Soumettre le CTVO » a son sélecteur de type depuis toujours ;
 * l'écran de vérification agence, lui, n'avait que la déduction.
 */
export type NatureChoisie = Extract<NatureEntite, 'particulier' | 'pm'>;

export interface EntityKindOverrides {
  vendeur?: NatureChoisie;
  acheteur?: NatureChoisie;
}

/**
 * Le choix humain prime sur la déduction, toujours. Sans choix, la règle
 * s'applique inchangée — y compris `'indecis'`, qui continue d'afficher les
 * deux blocs pour que quelqu'un tranche.
 */
export const resoudreNature = (deduite: NatureEntite, choisie?: NatureChoisie): NatureEntite => choisie ?? deduite;

/** Traduction vers la colonne `*_client_type` écrite en base. */
export const clientTypeDeNature = (nature: NatureChoisie): 'pro' | 'person' =>
  nature === 'pm' ? 'pro' : 'person';
